import * as React from "react";
import ModernCard from "../core/ModernCard";
import {
  patchCommission,
  addReviewComment,
} from "../../libs/commission_calculator";
import { toast } from "../../utils";
import Button from "../core/Button";
import ApprovalComments from "./ApprovalComments";
import * as moment from "moment";
import MonthStatusLabel from "./MonthStatusLabel";
import styled from "styled-components";
import * as Sentry from "@sentry/browser";

function ManagerMonthApproval({ commission, commissionPromise, history }) {
  const [settingApproval, setSettingApproval] = React.useState(false);
  const [submittingComment, setSubmittingComment] = React.useState(false);
  const [text, setText] = React.useState("");

  function submitComment(comment) {
    setSubmittingComment(true);
    return addReviewComment(commission.id, comment)
      .then(() => {
        commissionPromise.reload();
        setSubmittingComment(false);
        setText("");
      })
      .catch(e => {
        setSubmittingComment(false);
        toast.error("Error adding comment");
        Sentry.captureException(e);
      });
  }

  function updateApproval(approval: CommissionApprovalState) {
    setSettingApproval(true);
    patchCommission(commission.id, { userApproval: approval })
      .then(() => {
        if (text && text !== "") {
          submitComment(text);
        }
        commissionPromise.reload();
        setSettingApproval(false);
        toast.success("Changed commission status");
        if (history.length > 0) {
          history.goBack();
        } else {
          history.push("/");
        }
      })
      .catch(e => {
        setSettingApproval(false);
        toast.error("Error while updating approval.");
        throw e;
      });
  }

  let content;
  switch (commission.userApproval) {
    case "ready_for_approval":
      content = (
        <p>
          This commission was submitted to salesperson for approval. You can
          unsubmit by selecting button below.
        </p>
      );
      break;
    case "needs_review":
      content = (
        <p>
          This commission has been flagged for review. Please review and make
          corrections if necessary. Resubmit when you are finished.
        </p>
      );
      break;
    case "approved":
      content = (
        <p>
          This commission was approved on{" "}
          {moment(commission.approvedAt).format("MMM Do YYYY, h:mm:ss a")}.
        </p>
      );
      break;
  }

  return (
    <ModernCard title="Approval Status" ta="left" m="0px">
      <ApprovalStatusContainer>
        <ApprovalStatusControlColumn>
          <ApprovalStatusLabelContainer>
            Current status:{" "}
            <MonthStatusLabel userApprovalStatus={commission.userApproval} />
          </ApprovalStatusLabelContainer>
          {content}
          {commission.userApproval !== "approved" && (
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Comment here (optional)"
              style={{ width: "95%", height: "120px", margin: "10px" }}
            />
          )}

          <ApprovalButton
            userApprovalStatus={commission.userApproval}
            updateApproval={updateApproval}
            settingApproval={settingApproval || submittingComment}
          />
        </ApprovalStatusControlColumn>
        <ApprovalStatusCommentColumn>
          <ApprovalComments
            commission={commission}
            commissionPromise={commissionPromise}
            sortAscending={true}
          />
        </ApprovalStatusCommentColumn>
      </ApprovalStatusContainer>
    </ModernCard>
  );
}

const ApprovalButton = ({
  userApprovalStatus,
  updateApproval,
  settingApproval,
}) => {
  if (userApprovalStatus == "ready_for_approval") {
    return (
      <Button
        onClick={() => updateApproval("not_ready_for_approval")}
        loading={settingApproval}
        color="warning"
      >
        Set Not Ready for Approval
      </Button>
    );
  } else if (
    userApprovalStatus == "needs_review" ||
    userApprovalStatus == "not_ready_for_approval"
  ) {
    return (
      <Button
        onClick={() => updateApproval("ready_for_approval")}
        loading={settingApproval}
        color="success"
      >
        Set Ready for Approval
      </Button>
    );
  } else {
    return null;
  }
};

const ApprovalStatusContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
`;

const ApprovalStatusControlColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 30%;
  border-right: 1px dashed #d9d9d9;
  padding: 0 20px;
`;

const ApprovalStatusCommentColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 70%;
  padding: 0 20px;
  max-height: 300px;
  overflow: scroll;
`;

const ApprovalStatusLabelContainer = styled.div`
  display: flex;
  padding: 10px;
  margin-bottom: 10px;
  border-bottom: 1px dashed #d9d9d9;
  width: 100%;
`;

export default ManagerMonthApproval;



// WEBPACK FOOTER //
// ./app/javascript/components/commission_calculator/ManagerMonthApproval.tsx